import { StorageChainConfig } from '../../RackTypes'
import { getShelfSelection } from '../selection-utils'
import { getMaxZByHeight, getShelfSpacing, isPositionBlocked, shouldHideDumbbellShelf } from './mms-shelf-utils'

export type ShelfData = {
  hardwareSlotName: string // Adjusted for counting
  shelfType: string | undefined
}
export type UnitShelfData = {
  unit: number
  unitHeight: string
  shelves: ShelfData[]
  isEndUnit?: boolean
  adjacentToCorner?: boolean
}

export type HardwareCountData = {
  unitIndex: number
  hardwareSlotName: string
  shelfType: string | undefined
}

// Converts shelf data from all units into flat shelves[] for comparison.
export function convertUnitsToShelves(allUnitsShelves: {
  unit: number
  unitHeight: string
  isEndUnit?: boolean
  adjacentToCorner?: boolean
  shelves: { hardwareSlotName: string; shelfType: string | undefined }[]
}[]) {
  const resolved: HardwareCountData[] = []

  allUnitsShelves.forEach((unit) => {
    unit.shelves.forEach((shelf) => {
      resolved.push({
        unitIndex        : unit.unit,
        hardwareSlotName : shelf.hardwareSlotName,
        shelfType        : shelf.shelfType,
      })
    })
  })

  return resolved
}

export function getBaseShelfDataForHardware(shelfConfig: StorageChainConfig, isAdjacentTo100?: boolean): ShelfData[] {
  const shelfConfigs = [
    { id: 'shelf_one', selection: shelfConfig.shelfOneSelection, condition: true, range: [0, 0] },
    { id: 'shelf_two', selection: shelfConfig.shelfTwoSelection, condition: true, range: [18, 22] },
    { id: 'shelf_three', selection: shelfConfig.shelfThreeSelection, condition: true, range: [36, 44] },
    { id: 'shelf_four', selection: shelfConfig.shelfFourSelection, condition: shelfConfig.heightSelection === '70' || shelfConfig.heightSelection === '100', range: [54, 62] },
    { id: 'shelf_five', selection: shelfConfig.shelfFiveSelection, condition: shelfConfig.heightSelection === '100', range: [72, 92] },
  ]

  const maxZ = getMaxZByHeight(shelfConfig.heightSelection)
  const unitShelves: ShelfData[] = []

  // Keep track of occupied positions (same as assignShelfPlacement)
  const occupiedRanges: Array<{start: number, end: number}> = []

  shelfConfigs.forEach(({ id, selection, condition, range }, index) => {
    if (!condition || !selection) return

    // The rail crossmember is not offered on 70"-wide units
    if (getShelfSelection(selection) === 'rail' && shelfConfig.widthSelection === '70') return

    const shelfHeight = getShelfSpacing(selection)
    let targetZ = range[0] // Start with preferred position

    // Apply the same adjacency logic as assignShelfPlacement - adjust ALL shelves when adjacent to 100"
    if (isAdjacentTo100) {
      targetZ += 4
    }

    // If the preferred position is blocked, find the lowest available position
    while (isPositionBlocked(targetZ, shelfHeight, occupiedRanges) && targetZ <= maxZ) {
      targetZ += 1 // Move up by 1 unit until we find space
    }

    // Prevent placing this shelf if it would exceed the total allowed height
    if (targetZ + shelfHeight > maxZ) return

    // Mark this position as occupied
    occupiedRanges.push({ start: targetZ, end: targetZ + shelfHeight })

    // Calculate hardware slot name using the same logic as assignShelfPlacement
    const slotZ = targetZ
    const is100Height = shelfConfig.heightSelection === '100'
    const offsetZ = is100Height ? 0 : 0
    const hardwareSlotName = `${id}_${slotZ + offsetZ}h`

    // Handle dumbbell shelf replacement logic (same as assignShelfPlacement)
    let finalSelection = selection
    const shouldHide = shouldHideDumbbellShelf(
      shelfConfig.heightSelection,
      shelfConfig.shelfOneSelection,
      shelfConfig.shelfTwoSelection,
      shelfConfig.shelfThreeSelection,
      shelfConfig.shelfFourSelection,
      shelfConfig.shelfFiveSelection
    )

    if (shouldHide) {
      const is43Height = shelfConfig.heightSelection === '43'
      const is70Height = shelfConfig.heightSelection === '70'

      if ((is43Height && id === 'shelf_three') || (is70Height && id === 'shelf_four') || (is100Height && id === 'shelf_five')) {
        finalSelection = 'kettlebell shelf'
      }
    }

    const shelfType = getShelfSelection(finalSelection)
    unitShelves.push({ hardwareSlotName, shelfType })
  })

  return unitShelves
}

// Groups shelves by shelfIndex for row-wise logic or debugging.
export function getHardwareCount(allUnitsShelves: UnitShelfData[]) {
  const flat: HardwareCountData[] = convertUnitsToShelves(allUnitsShelves)

  const firstUnit = flat[0]?.unitIndex
  const lastUnitIndex = flat[flat.length - 1]?.unitIndex

  let sharedBoltCount = 0
  let soloBoltCount = 0

  for (let i = 0; i < flat.length; i += 1) {
    const thisShelf = flat[i]

    const thisUnitShelves = flat.filter(s => s?.unitIndex === thisShelf?.unitIndex)
    const previousUnitShelves = i > 0 ? flat.filter(s => s?.unitIndex === thisShelf?.unitIndex - 1) : undefined
    const nextUnitShelves = flat.filter(s => s?.unitIndex === thisShelf?.unitIndex + 1)

    // determine if this is an end shelf
    const isEndUnit = firstUnit === thisUnitShelves[0]?.unitIndex || lastUnitIndex === thisUnitShelves[0]?.unitIndex
    const hasCornerNeighbor = nextUnitShelves[0]?.shelfType === 'corner' || previousUnitShelves?.[0]?.shelfType === 'corner'

    let shelfBoltsCount = 4

    // Rail crossmembers get their own dedicated hardware and never share bolts — like corners.
    if (thisShelf.shelfType === 'rail') {
      soloBoltCount += shelfBoltsCount
      continue
    }

    if (isEndUnit || hasCornerNeighbor) {
      shelfBoltsCount -= 2
      soloBoltCount += 2
    }

    // A rail neighbor never shares, so it must not satisfy another shelf's shared match either.
    const hasNextSharedShelf = nextUnitShelves?.find(s => s.hardwareSlotName === thisShelf.hardwareSlotName && s.shelfType !== 'rail')
    const hasPreviousSharedShelf = previousUnitShelves?.find(s => s.hardwareSlotName === thisShelf.hardwareSlotName && s.shelfType !== 'rail')

    if (thisShelf.shelfType === 'dumbbell shelf') {
      // Dumbbell shelves require special handling. They will sometimes share 1/2 of the bolts, or all of them depending on if they are adjacent to another dumbbell shelf.
      if (hasNextSharedShelf) {
        if (hasNextSharedShelf?.shelfType === 'dumbbell shelf') {
          shelfBoltsCount -= 2
          sharedBoltCount += 2
        } else {
          sharedBoltCount -= 1
          sharedBoltCount += 1
        }
      }

      if (hasPreviousSharedShelf) {
        if (hasPreviousSharedShelf?.shelfType === 'dumbbell shelf') {
          shelfBoltsCount -= 2
          sharedBoltCount += 2
        } else {
          sharedBoltCount -= 1
          sharedBoltCount += 1
        }
      }
    } else {
      if (hasNextSharedShelf) {
        shelfBoltsCount -= 2
        sharedBoltCount += 2
      }

      if (shelfBoltsCount > 0 && hasPreviousSharedShelf) {
        shelfBoltsCount -= 2
        sharedBoltCount += 2
      }
    }

    if (shelfBoltsCount > 0) {
      soloBoltCount += shelfBoltsCount
    }
  }

  return {
    shared : Math.ceil((sharedBoltCount / 2) / 4),
    solo   : Math.ceil(soloBoltCount / 4),
  }
}
